import { useEffect, useState } from 'react';
import { useStore } from 'zustand';
import type { RoomId } from '@/core/content/ids';
import type { Game } from '@/game/Game';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { usePlacementUi, useSave } from '@/ui/services/ServicesContext';
import styles from './PlacementBar.module.css';
import { Icon } from './Icon';
import { currentRoom } from '@/ui/util/room';

const NOTICE_MS = 2600;
const NOTICE_KEYS = {
  boxLocked: 'ui.box.locked',
  cannotRotate: 'ui.place.cannot_rotate',
  holdToMove: 'ui.place.hold_to_move',
} as const;

/**
 * S25 while dragging: a small hint pill (valid / can't go here / just right) and Xoay / Hủy for a second finger.
 * Kind notices fade on their own. A selected item's actions live on its card (SelectionCard).
 */
export function PlacementBar({ game }: { game: Game }) {
  const { t } = useI18n();
  const dragging = usePlacementUi(game, (s) => s.dragging);
  const ghost = usePlacementUi(game, (s) => s.ghost);
  const reason = usePlacementUi(game, (s) => s.reason);
  const canRotate = usePlacementUi(game, (s) => s.canRotate);
  const room = useSave(currentRoom);
  const colorAssist = useSave((s) => s.settings.colorAssist);
  const notice = usePlacementUi(game, (s) => s.notice);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const visibleNotice = notice && notice.at !== dismissedAt ? notice : null;

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => {
      setDismissedAt(notice.at);
    }, NOTICE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [notice]);

  const miniGameRunning = useStore(game.miniGameUi, (s) => s.run !== null);
  if (miniGameRunning) return null;

  return (
    <div className={styles.wrap}>
      {dragging && ghost !== 'none' && (
        <div className={styles.pill} data-state={ghost} role="status">
          {colorAssist && (
            <Icon
              name={ghost === 'rightSpot' ? 'sparkle' : ghost === 'valid' ? 'check' : 'close'}
              size={20}
              className={styles.mark}
            />
          )}
          {ghost === 'rightSpot'
            ? t('ui.place.hint.right_spot')
            : ghost === 'valid'
              ? t('ui.place.hint.valid')
              : t(reasonKey(reason, room))}
        </div>
      )}
      {dragging && (
        // A second finger can turn or drop the item back while the first one keeps dragging (S25).
        <div className={styles.bar} role="toolbar" aria-label={t('ui.place.toolbar')}>
          {canRotate && (
            <button
              type="button"
              className={styles.button}
              onClick={() => {
                game.rotateSelection();
              }}
            >
              <Icon name="rotate" size={22} />
              {t('ui.place.rotate')}
            </button>
          )}
          <button
            type="button"
            className={styles.button}
            data-testid="placement-cancel"
            onClick={() => {
              game.cancelPlacement();
            }}
          >
            <Icon name="close" size={22} />
            {t('ui.place.cancel')}
          </button>
        </div>
      )}
      {!dragging && visibleNotice && (
        <div className={styles.pill} data-state="notice" role="status">
          {t(NOTICE_KEYS[visibleNotice.kind])}
        </div>
      )}
    </div>
  );
}

/** A kind line for each reason a spot is refused (M1-02); unknown ones keep the gentle generic line. */
function reasonKey(reason: string | null, room: RoomId): string {
  switch (reason) {
    case 'OUT_OF_BOUNDS':
    case 'OVERLAP':
    case 'OPENING':
    case 'SLOT_OCCUPIED':
    case 'NOT_ACCEPTED':
    case 'LAMP_ON_TOP':
    case 'WRONG_ROOM_FOR_KIND':
      return `ui.place.reason.${reason.toLowerCase()}`;
    case 'BLOCKED_ZONE':
      return room === 'minimart' ? 'ui.place.reason.aisle' : 'ui.place.reason.door';
    default:
      return 'ui.place.hint.invalid';
  }
}
